import { Controller } from "@hotwired/stimulus"

const PREFECTURES = [
  {
    name: "石川県",
    reading: "いしかわけん",
    municipalities: [
      ["金沢市", "かなざわし"],
      ["七尾市", "ななおし"],
      ["小松市", "こまつし"],
      ["輪島市", "わじまし"],
      ["珠洲市", "すずし"],
      ["加賀市", "かがし"],
      ["羽咋市", "はくいし"],
      ["かほく市", "かほくし"],
      ["白山市", "はくさんし"],
      ["能美市", "のみし"],
      ["野々市市", "ののいちし"],
      ["川北町", "かわきたまち"],
      ["津幡町", "つばたまち"],
      ["内灘町", "うちなだまち"],
      ["志賀町", "しかまち"],
      ["宝達志水町", "ほうだつしみずちょう"],
      ["中能登町", "なかのとまち"],
      ["穴水町", "あなみずまち"],
      ["能登町", "のとちょう"]
    ]
  },
  {
    name: "富山県",
    reading: "とやまけん",
    municipalities: [
      ["富山市", "とやまし"],
      ["高岡市", "たかおかし"],
      ["魚津市", "うおづし"],
      ["氷見市", "ひみし"],
      ["滑川市", "なめりかわし"],
      ["黒部市", "くろべし"],
      ["砺波市", "となみし"],
      ["小矢部市", "おやべし"],
      ["南砺市", "なんとし"],
      ["射水市", "いみずし"],
      ["舟橋村", "ふなはしむら"],
      ["上市町", "かみいちまち"],
      ["立山町", "たてやままち"],
      ["入善町", "にゅうぜんまち"],
      ["朝日町", "あさひまち"]
    ]
  },
  {
    name: "福井県",
    reading: "ふくいけん",
    municipalities: [
      ["福井市", "ふくいし"],
      ["敦賀市", "つるがし"],
      ["小浜市", "おばまし"],
      ["大野市", "おおのし"],
      ["勝山市", "かつやまし"],
      ["鯖江市", "さばえし"],
      ["あわら市", "あわらし"],
      ["越前市", "えちぜんし"],
      ["坂井市", "さかいし"],
      ["永平寺町", "えいへいじちょう"],
      ["池田町", "いけだちょう"],
      ["南越前町", "みなみえちぜんちょう"],
      ["越前町", "えちぜんちょう"],
      ["美浜町", "みはまちょう"],
      ["高浜町", "たかはまちょう"],
      ["おおい町", "おおいちょう"],
      ["若狭町", "わかさちょう"]
    ]
  }
]

const LOCATION_OPTIONS = PREFECTURES.flatMap((prefecture) => [
  {
    label: prefecture.name,
    prefecture: prefecture.name,
    municipality: "",
    reading: prefecture.reading,
    municipalityReading: ""
  },
  ...prefecture.municipalities.map(([municipality, reading]) => ({
    label: `${prefecture.name}${municipality}`,
    prefecture: prefecture.name,
    municipality,
    reading: `${prefecture.reading}${reading}`,
    municipalityReading: reading
  }))
])

export default class extends Controller {
  static targets = ["input", "list"]

  connect() {
    this.activeIndex = -1
    this.isComposing = false
    this.suggestions = []
    this.hide()
  }

  disconnect() {
    window.clearTimeout(this.blurTimer)
  }

  input() {
    if (!this.hasInputTarget) return
    this.update()
  }

  focus() {
    if (!this.hasInputTarget) return
    this.update()
  }

  keydown(event) {
    if (this.isComposing || event.isComposing) return
    if (!this.hasListTarget) return
    if (this.listTarget.classList.contains("hidden")) return

    if (event.key === "ArrowDown") {
      event.preventDefault()
      this.move(1)
    } else if (event.key === "ArrowUp") {
      event.preventDefault()
      this.move(-1)
    } else if (event.key === "Enter" && this.activeIndex >= 0) {
      event.preventDefault()
      this.select(this.suggestions[this.activeIndex])
    } else if (event.key === "Escape") {
      this.hide()
    }
  }

  compositionStart() {
    this.isComposing = true
  }

  compositionUpdate(event) {
    if (!this.hasInputTarget) return
    this.update(event.data || this.inputTarget.value)
  }

  compositionEnd() {
    this.isComposing = false
    if (!this.hasInputTarget) return
    this.update()
  }

  blur() {
    window.clearTimeout(this.blurTimer)
    this.blurTimer = window.setTimeout(() => this.hide(), 120)
  }

  choose(event) {
    event.preventDefault()

    const option = this.suggestions[Number(event.currentTarget.dataset.locationSuggestIndex)]
    if (option) this.select(option)
  }

  update(value) {
    if (!this.hasInputTarget || !this.hasListTarget) return

    const query = this.normalize(value ?? this.inputTarget.value)
    if (query.length < 2) {
      this.hide()
      return
    }

    this.suggestions = LOCATION_OPTIONS
      .map((option) => ({ option, score: this.score(option, query) }))
      .filter((result) => result.score > 0)
      .sort((a, b) => b.score - a.score || a.option.label.length - b.option.label.length)
      .slice(0, 7)
      .map((result) => result.option)

    if (this.suggestions.length === 0) {
      this.hide()
      return
    }

    this.activeIndex = -1
    this.render()
  }

  score(option, query) {
    const label = this.normalize(option.label)
    const reading = this.normalize(option.reading)
    const prefecture = this.normalize(option.prefecture)
    const municipality = this.normalize(option.municipality)
    const municipalityReading = this.normalize(option.municipalityReading)

    if (label === query || reading === query) return 100
    if (label.startsWith(query) || reading.startsWith(query)) return 80
    if (label.includes(query) || reading.includes(query)) return 60

    if (query.startsWith(prefecture)) {
      const remaining = query.slice(prefecture.length)
      if (remaining.length === 0) return option.municipality ? 30 : 90
      if (municipality.startsWith(remaining) || municipalityReading.startsWith(remaining)) return 75
      if (municipality.includes(remaining) || municipalityReading.includes(remaining)) return 50
    }

    return 0
  }

  render() {
    if (!this.hasInputTarget || !this.hasListTarget) return

    this.listTarget.innerHTML = this.suggestions.map((option, index) => `
      <button type="button"
              class="location-suggest__option"
              role="option"
              data-location-suggest-index="${index}"
              data-action="pointerdown->location-suggest#choose">
        <span class="location-suggest__option-label">${option.label}</span>
      </button>
    `).join("")

    this.listTarget.classList.remove("hidden")
    this.inputTarget.setAttribute("aria-expanded", "true")
  }

  move(direction) {
    if (!this.hasListTarget || this.suggestions.length === 0) return

    this.activeIndex = (this.activeIndex + direction + this.suggestions.length) % this.suggestions.length

    this.listTarget.querySelectorAll(".location-suggest__option").forEach((element, index) => {
      element.classList.toggle("location-suggest__option--active", index === this.activeIndex)
    })
  }

  select(option) {
    if (!option || !this.hasInputTarget) return

    this.inputTarget.value = option.label
    this.inputTarget.dispatchEvent(new Event("input", { bubbles: true }))
    this.inputTarget.dispatchEvent(new Event("change", { bubbles: true }))
    this.hide()
    this.inputTarget.focus()
  }

  hide() {
    if (this.hasListTarget) {
      this.listTarget.classList.add("hidden")
      this.listTarget.innerHTML = ""
    }

    if (this.hasInputTarget) {
      this.inputTarget.setAttribute("aria-expanded", "false")
    }

    this.activeIndex = -1
  }

  normalize(value) {
    return String(value ?? "")
      .toString()
      .trim()
      .replace(/\s+/g, "")
      .replace(/[ァ-ン]/g, (char) => String.fromCharCode(char.charCodeAt(0) - 0x60))
      .toLowerCase()
  }
}
